/**
 * Activity drawer tab that lists evidence linked to one activity. Remove
 * detaches the link for THIS activity only — a deduped file shared by
 * other activities stays intact on those. Global delete lives on the
 * evidence library page.
 */

import { Button } from '@g17eco/ui-modern/components/ui/button';
import { generateToast, toastFromError } from '@g17eco/molecules/toasts';

import {
  useDetachEvidenceFromActivityMutation,
  useListEmissionEvidenceQuery,
} from '../api/emissions';
import { useDownloadEvidence } from '../hooks/useDownloadEvidence';
import type { EmissionEvidence } from '../types/emissions';
import { EVIDENCE_TYPE_LABEL, getMimeIcon } from '../utils/evidenceDisplay';
import { Card } from 'reactstrap';
import { CardContent } from '@g17eco/ui-modern/components/ui/card';

interface EvidenceTabProps {
  initiativeId: string;
  activityId: string;
}

export const EvidenceTab = ({ initiativeId, activityId }: EvidenceTabProps) => {
  const { data, isLoading, error: listError } = useListEmissionEvidenceQuery(
    { initiativeId, activityId },
    { skip: !initiativeId || !activityId }
  );
  const [detachEvidence, { isLoading: isDetaching }] = useDetachEvidenceFromActivityMutation();
  const { download } = useDownloadEvidence(initiativeId);

  const items = data?.items ?? [];

  const handleRemove = async (evidenceId: string) => {
    try {
      const { detached } = await detachEvidence({ initiativeId, evidenceId, activityId }).unwrap();
      if (!detached) {
        // Server reports the link was already gone (another tab, stale cache,
        // etc). Surface it so the click doesn't look like a silent no-op.
        generateToast({ color: 'warning', message: 'Evidence was already removed from this activity.' });
      }
    } catch (error) {
      toastFromError({ error, title: 'Failed to remove evidence' });
    }
  };

  if (isLoading) {
    return <div className='tw:text-sm tw:text-ThemeTextMedium'>Loading evidence…</div>;
  }

  if (listError) {
    // Distinguish a genuine empty state from a failed list query — otherwise
    // the 5xx/auth-drop case silently masquerades as "no evidence attached"
    // and the user never knows their files are still there.
    return (
      <div className='tw:text-sm tw:text-ThemeAccentDanger' role='alert'>
        Couldn't load evidence. Try refreshing the page.
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <Card>
        <CardContent className='tw:text-sm tw:p-5 tw:border-dashed tw:border-1 tw:border-border'>
          No evidence attached. Use the activity form to upload supporting documents.
        </CardContent>
      </Card>
    );
  }

  return (
    <ul className='tw:divide-y tw:divide-border tw:rounded tw:border tw:border-border'>
      {items.map((item) => (
        <EvidenceRow
          key={item._id}
          evidence={item}
          onDownload={() => download(item._id)}
          onRemove={() => handleRemove(item._id)}
          isRemoving={isDetaching}
        />
      ))}
    </ul>
  );
};

interface EvidenceRowProps {
  evidence: EmissionEvidence;
  onDownload: () => void;
  onRemove: () => void;
  isRemoving: boolean;
}

const EvidenceRow = ({ evidence, onDownload, onRemove, isRemoving }: EvidenceRowProps) => (
  <li className='tw:flex tw:items-center tw:gap-3 tw:p-3'>
    <i className={`fa-regular ${getMimeIcon(evidence.mimeType)} tw:text-ThemeTextMedium`} aria-hidden />
    <div className='tw:flex-1 tw:min-w-0'>
      <div className='tw:truncate tw:text-sm tw:font-medium'>{evidence.originalName}</div>
      <div className='tw:text-xs tw:text-ThemeTextLight'>
        {EVIDENCE_TYPE_LABEL[evidence.evidenceType]}
        {evidence.documentDate ? ` · ${evidence.documentDate.slice(0, 10)}` : ''}
        {evidence.periodStart && evidence.periodEnd
          ? ` · ${evidence.periodStart.slice(0, 10)} – ${evidence.periodEnd.slice(0, 10)}`
          : ''}
        {evidence.issuer ? ` · ${evidence.issuer}` : ''}
      </div>
    </div>
    <Button
      variant='ghost'
      size='icon-sm'
      onClick={onDownload}
      aria-label={`Download ${evidence.originalName}`}
    >
      <i className='fa fa-download' />
    </Button>
    <Button
      variant='ghost'
      size='icon-sm'
      onClick={onRemove}
      disabled={isRemoving}
      aria-label={`Remove ${evidence.originalName}`}
    >
      <i className='fa fa-times tw:text-ThemeTextLight' />
    </Button>
  </li>
);

